//Frameworks
import React, { useEffect, useImperativeHandle, useRef, forwardRef } from 'react';

//Drag&Drop plugin
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

//Legacy code
import listService from "ListService";
import eventBus from 'EventBus';
import Backbone from 'backbone';

//Wizard related react components
import WizardHeader from './WizardHeader';
import DesktopViewPanel from './DesktopViewPanel';
import MobileAppPanel from './MobileAppPanel';
import StylingPanel from './StylingPanel';
import WizardFooter from './WizardFooter';
import Intro from './Intro';

//Hooks
import { WizardContext } from './hooks/WizardContext';
import { useWizardData } from './hooks/useWizardData';
import { useSelectionManager } from './hooks/uiInteractions/useSelectionManager';
import { usePanelManager } from './hooks/uiInteractions/usePanelManager';
import { useWizardActions } from './hooks/uiInteractions/useWizardActions';
import { useWizardSyncAndSave } from './hooks/useWizardSyncAndSave';

//Helpers
import Icon from '../Common/Icon';
import ConfirmModal from '../Common/ConfirmModal';

//Styles
import * as MW from './styles/MobileWizard.styles';


const MobileWizard = forwardRef(({ initialData, setIsDirty, requestClose, fromLeftPanel }, ref) => {
    // Decoupled logic via custom hooks. Each hook manages a separate concern.
    // Data hook. Manages all backend data and state operations.
    const wizardData = useWizardData({ initialData, parentSetIsDirty: setIsDirty });
    const { save } = useWizardSyncAndSave({
        layout: wizardData.layout,
        globalStyles: wizardData.globalStyles,
        initialStateRef: wizardData.initialState,
        setIsDirty: wizardData.setIsDirty
    });
    const selection = useSelectionManager();
    const panels = usePanelManager();
    const actions = useWizardActions({
        save: save,
        layout: wizardData.layout,
        setIsDirty: wizardData.setIsDirty,
        requestClose,
        activePage: wizardData.pages.find(p => p.id === wizardData.activePageId),
        selectedSectionId: selection.selectedSectionId,
        selectedControlId: selection.selectedControlId,
        availableControls: wizardData.availableControls,
        addAllControls: wizardData.addAllControls,
        removeAllControls: wizardData.removeAllControls,
        deleteSection: wizardData.deleteSection,
        removeControl: wizardData.removeControl,
        moveControlUp: wizardData.moveControlUp,
        moveControlDown: wizardData.moveControlDown,
        moveSectionUp: wizardData.moveSectionUp,
        moveSectionDown: wizardData.moveSectionDown,
        setSelectedSectionId: selection.selectSection,
        orientation: panels.orientation,
        device: panels.device
    });

    const { pages, activePageId, setActivePageId, isDirty, layout } = wizardData;



    const activePage = pages.find(p => p.id === activePageId);

    // Connects to parent modal and handles cleanup.
    useImperativeHandle(ref, () => ({
        save: actions.handleSaveClick,
    }));

    useEffect(() => {
        if (setIsDirty) setIsDirty(isDirty);
    }, [isDirty, setIsDirty]);

    useEffect(() => {
        // ComponentWillUnmount cleanup logic
        return () => {

            const wasCompleted = listService.mobileGetWizardCompleted;
            if (wasCompleted) {
                listService.linkClicked = true;
                Backbone.history.loadUrl('mobile');
                eventBus.trigger('changeMobileLayout');
            }
        };
    }, []); // Empty dependency array ensures this runs only on unmount

    // Creates the context value for child components.
    const contextValue = {
        ...wizardData,
        ...selection,
        ...panels,
        ...actions,
        save: save,
        activePage,
    };

    return (
        <DndProvider backend={HTML5Backend}>
            <WizardContext.Provider value={contextValue}>
                <MW.WizardWrapper onClick={selection.deselectAll} onContextMenu={(e) => e.preventDefault()}>

                    {actions.isBackgrounded && (
                        <style>{`
                            .react-modal-container {
                                z-index: 99999 !important;
                            }
                        `}</style>
                    )}

                    <WizardHeader pages={pages} activePageId={activePageId} onPageChange={setActivePageId} />
                    <MW.WizardBody>
                        {!panels.isDesktopPanelOpen && <MW.PanelOpener side="left" onClick={panels.toggleDesktopPanel}><Icon name="desktop" color="#ffffff" /></MW.PanelOpener>}
                        {!panels.isStylingPanelOpen && <MW.PanelOpener side="right" onClick={panels.toggleStylingPanel}><Icon name="style" color="#ffffff" /></MW.PanelOpener>}

                        <MW.LeftPanelContainer isOpen={panels.isDesktopPanelOpen}>
                            <DesktopViewPanel onClose={panels.toggleDesktopPanel} />
                        </MW.LeftPanelContainer>

                        <MW.MainContent>
                            <MobileAppPanel />
                        </MW.MainContent>

                        <MW.RightPanelContainer isOpen={panels.isStylingPanelOpen}>
                            <StylingPanel onClose={panels.toggleStylingPanel} />
                        </MW.RightPanelContainer>
                    </MW.WizardBody>

                    <WizardFooter
                        onSaveClick={actions.handleSaveClick}
                        onFinishClick={actions.handleFinishClick}
                        isSaving={actions.isSaving}
                        onCloseClick={requestClose}
                    />
                </MW.WizardWrapper>
                {actions.confirmModal && <ConfirmModal {...actions.confirmModal} />}
            </WizardContext.Provider>
        </DndProvider>
    );
});

export default MobileWizard;